/* eslint-disable max-lines */
/* eslint-disable complexity */

import { createAction } from '@reduxjs/toolkit';
import {
  IAppointment,
  IAppointmentByKey,
  IPackageAppointments,
  IVehicleForRequest,
} from '../../../api/types';
import {
  EConsultantRole,
  IAppointmentsRequest,
  ICheckPodRequest,
  TScheduler,
  TServiceBook,
  TServiceConsultant,
} from './types';
import { AppThunk, IPageRequest, TArgCallback, TCallback } from '../../../types/types';
import { TScreen } from '../../../types/screens';
import { API } from '../../../api/api';
import { EServiceType } from '../appointmentFrameReducer/types';
import { EAppointmentTimingType } from '../appointment/types';
import {
  setAppointmentSaving,
  setConsultants,
  setCurrentFrameScreen,
  setSelectedRecalls,
} from '../appointmentFrameReducer/actions';
import { setChangesCompletedOpen, setSlotsWarningOpen } from '../modals/actions';
import { getCategories, getVehicleData, mapRecallsForRequest } from '../../../utils/utils';
import { Api } from '../../../api/ApiEndpoints/ApiEndpoints';
import {
  getAppointmentSlots,
  getServiceValetSlots,
  selectAppointment,
  selectServiceValetAppointment,
  setProfileLoading,
  getServiceCenterProfile,
} from '../appointment/actions';
import { IServiceCenterProfile } from '../appointment/types';
import { getRecallsByVin } from '../recall/actions';
import {
  collectServiceRequestIds,
  getRecallCategoryId,
  getSelectedCategoriesWithRequests,
} from '../../../utils/collectServiceRequestIds';

export const getAppointments = createAction<IAppointment[]>('Appointments/GetAppointments');
export const getAllAppointments = createAction<IAppointment[]>('Appointments/GetAllAppointments');
export const setAppointmentsLoading = createAction<boolean>('Appointments/SetAppointmentsLoading');
export const setAppointmentsModalLoading = createAction<boolean>(
  'Appointments/SetAppointmentsModalLoading'
);
export const setAppointmentsCount = createAction<number>('Appointments/SetAppointmentsCount');
export const setAllAppointmentsCount = createAction<number>('Appointments/SetAllAppointmentsCount');
export const getPackageByVehicle = createAction<IPackageAppointments[]>(
  'Appointments/GetPackageByVehicle'
);
export const getServiceBookList = createAction<TServiceBook[]>('Appointments/GetServiceBookList');
export const getScheduler = createAction<TScheduler[]>('Appointments/GetSchedulerList');
export const getAppointmentsPageData = createAction<Partial<IPageRequest>>(
  'Appointments/GetPageData'
);
export const getAdvisorsList = createAction<TServiceConsultant[]>(
  'Appointments/GetServiceAdvisors'
);
export const getTechnicians = createAction<TServiceConsultant[]>('Appointments/GetTechnicians');
export const getCurrentAppointment = createAction<IAppointmentByKey | null>(
  'Appointments/getCurrentAppointment'
);
export const setCurrentAppointmentLoading = createAction<boolean>(
  'Appointments/SetCurrentAppointmentLoading'
);

export const loadAppointments =
  (data: IAppointmentsRequest): AppThunk =>
  dispatch => {
    dispatch(setAppointmentsLoading(true));
    API.appointment
      .list(data)
      .then(({ data: { paging, result } }) => {
        if (data.pageIndex === 0 && data.pageSize === 0 && !data.startDate && !data.endDate) {
          dispatch(getAllAppointments(result));
          dispatch(setAllAppointmentsCount(paging.numberOfRecords));
        } else {
          dispatch(getAppointments(result));
          dispatch(setAppointmentsCount(paging.numberOfRecords));
        }
      })
      .catch(err => {
        console.log('load appointments for calendar', err);
      })
      .finally(() => dispatch(setAppointmentsLoading(false)));
  };

export const checkPodChanged =
  (
    serviceCenterId: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: TArgCallback<any>,
    onPodKept?: TCallback,
    onPodChanged?: TCallback
  ): AppThunk =>
  (dispatch, getState) => {
    const appointmentFrame = getState().appointmentFrame;
    const appointment = getState().appointment;
    const { isTransportationAvailable, isAppointmentTimingAvailable, isAdvisorAvailable } =
      getState().bookingFlowConfig;
    const { wasWarningShowed } = getState().modals;

    // todo check the case when the pod has changed
    const categories = getState().categories;

    const [make, model, year] = getVehicleData(
      appointmentFrame.selectedVehicle,
      appointmentFrame.valueService
    );

    const vehicle: IVehicleForRequest = {
      ...(appointmentFrame.selectedVehicle ?? {}),
      engineTypeId: appointmentFrame.selectedVehicle?.engineTypeId
        ? Number(appointmentFrame.selectedVehicle?.engineTypeId)
        : null,
      model: model ?? '',
      make: make ?? '',
      year: year ? +year : null,
      vin: appointmentFrame.selectedVehicle?.vin ?? '',
      mileage: appointmentFrame?.selectedVehicle?.mileage ?? null,
      appointmentHashKeys: [],
    };

    delete vehicle.appointmentHashKeys;

    const appointmentTimingType =
      appointmentFrame.serviceTypeOption?.type !== EServiceType.PickUpDropOff &&
      appointmentFrame.selectedTiming
        ? appointmentFrame.selectedTiming
        : EAppointmentTimingType.FirstAvailable;

    const serviceRequestIds = collectServiceRequestIds(
      appointmentFrame.service,
      appointmentFrame.subService,
      appointmentFrame.selectedPackage,
      appointment.selectedSR,
      undefined,
      appointment.selectedSRComments,
      getSelectedCategoriesWithRequests(
        categories.allCategories,
        appointmentFrame.serviceCategories
      ),
      appointmentFrame.appointmentByKey?.serviceRequests,
      appointmentFrame.appointmentByKey ? categories.allCategories : undefined
    );
    const maintenancePackageOption = appointmentFrame.selectedPackage
      ? { id: appointmentFrame.selectedPackage?.id, priceType: appointmentFrame.packagePricingType }
      : appointmentFrame.packageEMenuType !== null
        ? { optionType: appointmentFrame.packageEMenuType }
        : null;

    const data: ICheckPodRequest = {
      serviceRequests: serviceRequestIds,
      serviceCategories: getCategories(
        categories.allCategories,
        appointmentFrame.serviceCategories
      ),
      recalls: mapRecallsForRequest(
        appointmentFrame.selectedRecalls,
        getRecallCategoryId(
          appointmentFrame.service,
          appointmentFrame.subService,
          categories.allCategories,
          appointmentFrame.serviceCategories
        )
      ),
      maintenancePackageOption,
      appointmentTimingType,
      serviceCenterId,
      serviceTypeOptionId: appointmentFrame.serviceTypeOption?.id ?? null,
      zipCode: appointmentFrame.zipCode ?? null,
      address: null,
      advisorId: appointmentFrame.advisor?.id ?? null,
      vehicle,
      transportationOptionId:
        (appointmentFrame.serviceTypeOption?.type === EServiceType.VisitCenter ||
          !appointmentFrame.serviceTypeOption) &&
        !appointmentFrame.serviceTypeOption?.transportationOption &&
        appointmentFrame.transportation
          ? appointmentFrame.transportation?.id
          : null,
    };
    if (appointmentFrame?.address) {
      if (typeof appointmentFrame?.address === 'string') {
        data.address = {
          address: appointmentFrame?.streetName.trim(),
          city: appointmentFrame?.city.trim(),
          state: appointmentFrame?.politicalState,
          zipCode: appointmentFrame?.zipCode.trim(),
        };
      }
    }
    if (appointmentFrame?.appointmentByKey?.hashKey) {
      dispatch(setAppointmentSaving(true));
      Api.call(Api.endpoints.Appointments.CheckPodChanged, {
        data,
        urlParams: { key: appointmentFrame?.appointmentByKey?.hashKey },
      })
        .then(result => {
          if (result?.data) {
            if (wasWarningShowed) {
              const nextScreen: TScreen =
                isAdvisorAvailable && appointmentFrame.currentScreen !== 'consultantSelection'
                  ? 'consultantSelection'
                  : isTransportationAvailable &&
                      appointmentFrame.currentScreen !== 'transportationNeeds'
                    ? 'transportationNeeds'
                    : isAppointmentTimingAvailable
                      ? 'appointmentTiming'
                      : 'appointmentSelection';
              dispatch(setCurrentFrameScreen(nextScreen));
              if (onPodChanged) {
                onPodChanged();
              }
            } else {
              dispatch(setSlotsWarningOpen(true));
            }
          } else {
            if (onPodKept) {
              onPodKept();
            } else {
              dispatch(setChangesCompletedOpen(true));
            }
          }
        })
        .catch(e => {
          console.log(e);
          onError(e);
        })
        .finally(() => {
          dispatch(setAppointmentSaving(false));
        });
    }
  };

export const loadServiceBookList =
  (id: number): AppThunk =>
  dispatch => {
    dispatch(setAppointmentsLoading(true));
    Api.call(Api.endpoints.Appointments.GetServiceBooks, { urlParams: { id } })
      .then(result => {
        if (result) dispatch(getServiceBookList(result.data));
      })
      .catch(e => {
        console.log('get service book list error', e);
      });
  };

export const loadSchedulerList = (): AppThunk => dispatch => {
  dispatch(setAppointmentsLoading(true));
  Api.call(Api.endpoints.Appointments.GetSchedulers)
    .then(result => {
      if (result) dispatch(getScheduler(result.data));
    })
    .catch(e => {
      console.log('get scheduler list error', e);
    });
};

export const loadServiceConsultants =
  (serviceCenterId: number, isActive?: boolean): AppThunk =>
  dispatch => {
    dispatch(setAppointmentsLoading(true));
    Api.call<TServiceConsultant[]>(Api.endpoints.ServiceConsultants.GetByRole, {
      params: { serviceCenterId, roles: ['Advisor', 'Technician'], showActive: isActive },
    })
      .then(result => {
        if (result?.data) {
          dispatch(
            getAdvisorsList(result.data.filter(item => item.role === EConsultantRole.Advisor))
          );
          dispatch(
            getTechnicians(result.data.filter(item => item.role === EConsultantRole.Technician))
          );
        }
      })
      .catch(e => {
        console.log('load Service Consultants error', e);
      });
  };

export const clearAfterCloning = (): AppThunk => dispatch => {
  dispatch(getCurrentAppointment(null));
  dispatch(selectAppointment(null));
  dispatch(selectServiceValetAppointment(null));
  dispatch(setConsultants([]));
  dispatch(getAppointmentSlots([]));
  dispatch(getServiceValetSlots([]));
  dispatch(getRecallsByVin([]));
  dispatch(setSelectedRecalls([]));
};

export const loadSCProfile =
  (id: number): AppThunk =>
  async dispatch => {
    dispatch(setProfileLoading(true));
    try {
      const { data } = await Api.call<IServiceCenterProfile>(
        Api.endpoints.ServiceCenters.Retrieve,
        { urlParams: { id } }
      );
      dispatch(getServiceCenterProfile(data));
    } catch (err) {
      console.log('load sc profile err', err);
    } finally {
      dispatch(setProfileLoading(false));
    }
  };
